import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { uploadEvidenceForSignal, getEvidenceForSignal, getEvidenceForSimulation, deleteEvidence } from '../utils/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import { config } from '../config/index';
import { getActingOrg } from '../utils/actingOrg';

export interface EvidenceItem {
  id: string;
  signal_id: string;
  organization_id: string;
  filename: string;
  file_size: number;
  content_type: string;
  file_path: string;
  parsed_summary?: { summary?: string } | null;
  structured_text?: string | null;
  document_type?: string | null;
  parsing_status: 'pending' | 'processing' | 'completed' | 'failed';
  parsing_error?: string | null;
  upload_metadata?: Record<string, any> | null;
  created_at?: string | null;
  updated_at?: string | null;
  used_in_reasoning?: boolean;
}

export interface UploadProgress {
  fileId: string;
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  error?: string;
}

interface UseEvidenceUploadOptions {
  signalId: string;
  simulationId?: string;
  onEvidenceUploaded?: (evidenceId: string) => void;
  onEvidenceProcessing?: (evidenceId: string) => void;
  onEvidenceCompleted?: (evidenceId: string) => void;
  onEvidenceFailed?: (evidenceId: string, error: string) => void;
}

export const useEvidenceUpload = (options: UseEvidenceUploadOptions) => {
  const { signalId, simulationId, onEvidenceUploaded, onEvidenceProcessing, onEvidenceCompleted, onEvidenceFailed } = options;
  
  const [evidence, setEvidence] = useState<EvidenceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState<Map<string, UploadProgress>>(new Map());
  const { subscribe, unsubscribe, addMessageListener } = useWebSocket();
  const progressMapRef = useRef<Map<string, UploadProgress>>(new Map());

  // Fetch evidence list - use simulation endpoint if simulationId provided to get used_in_reasoning flags
  const fetchEvidence = useCallback(async () => {
    try {
      setLoading(true);
      const data = simulationId 
        ? await getEvidenceForSimulation(simulationId)
        : await getEvidenceForSignal(signalId);
      setEvidence(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Failed to fetch evidence:', error);
      toast.error('Failed to load evidence');
    } finally {
      setLoading(false);
    }
  }, [signalId, simulationId]);

  // Initial fetch
  useEffect(() => {
    fetchEvidence();
  }, [fetchEvidence]);

  // Subscribe to WebSocket updates
  useEffect(() => {
    const handleMessage = (message: any) => {
      const messageType = message.type || message.data?.type;
      
      if (messageType?.startsWith('evidence.')) {
        const evidenceId = message.evidence_id || message.data?.evidence_id;
        const signalIdFromMessage = message.signal_id || message.data?.signal_id;
        
        // Only process messages for this signal
        if (signalIdFromMessage !== signalId) return;
        
        if (messageType === 'evidence.uploaded') {
          // Refresh evidence list
          fetchEvidence();
          if (onEvidenceUploaded && evidenceId) {
            onEvidenceUploaded(evidenceId);
          }
        } else if (messageType === 'evidence.processing') {
          // Update progress
          const progress = progressMapRef.current.get(evidenceId);
          if (progress) {
            progress.status = 'processing';
            progressMapRef.current.set(evidenceId, progress);
            setUploadProgress(new Map(progressMapRef.current));
          }
          if (onEvidenceProcessing && evidenceId) {
            onEvidenceProcessing(evidenceId);
          }
        } else if (messageType === 'evidence.completed') {
          // Refresh evidence list and remove from progress
          fetchEvidence();
          progressMapRef.current.delete(evidenceId);
          setUploadProgress(new Map(progressMapRef.current));
          toast.success(`Evidence "${message.data?.filename || 'file'}" processed successfully`);
          if (onEvidenceCompleted && evidenceId) {
            onEvidenceCompleted(evidenceId);
          }
        } else if (messageType === 'evidence.failed') {
          // Update progress with error
          const progress = progressMapRef.current.get(evidenceId);
          if (progress) {
            progress.status = 'failed';
            progress.error = message.error || message.data?.error || 'Processing failed';
            progressMapRef.current.set(evidenceId, progress);
            setUploadProgress(new Map(progressMapRef.current));
          }
          toast.error(`Failed to process evidence: ${message.error || message.data?.error || 'Unknown error'}`);
          if (onEvidenceFailed && evidenceId) {
            onEvidenceFailed(evidenceId, message.error || message.data?.error || 'Unknown error');
          }
        }
      }
    };

    const removeListener = addMessageListener(handleMessage);
    
    // Subscribe to signal-specific channel
    subscribe('signal', signalId);
    
    return () => {
      unsubscribe('signal', signalId);
      removeListener();
    };
  }, [signalId, subscribe, unsubscribe, addMessageListener, fetchEvidence, onEvidenceUploaded, onEvidenceProcessing, onEvidenceCompleted, onEvidenceFailed]);

  // Upload evidence with progress tracking
  const uploadEvidence = useCallback(async (file: File) => {
    // Validate file size (100MB)
    const MAX_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error('File size exceeds 100MB limit');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/json',
      'text/plain', 'text/csv'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error(`File type ${file.type} not supported`);
      return;
    }

    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const progress: UploadProgress = {
      fileId: tempId,
      fileName: file.name,
      progress: 0,
      status: 'uploading'
    };

    progressMapRef.current.set(tempId, progress);
    setUploadProgress(new Map(progressMapRef.current));

    try {
      // Create upload with progress tracking
      const xhr = new XMLHttpRequest();
      
      return new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            progress.progress = percentComplete;
            progressMapRef.current.set(tempId, progress);
            setUploadProgress(new Map(progressMapRef.current));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 201) {
            try {
              const response = JSON.parse(xhr.responseText);
              // Replace temp ID with real evidence ID
              progressMapRef.current.delete(tempId);
              if (response.id) {
                progress.fileId = response.id;
                progress.status = 'processing';
                progress.progress = 100;
                progressMapRef.current.set(response.id, progress);
                setUploadProgress(new Map(progressMapRef.current));
              }
              
              // Refresh evidence list (async)
              fetchEvidence().then(() => {
                toast.success(`Evidence "${file.name}" uploaded successfully`);
                resolve();
              }).catch((error) => {
                reject(error);
              });
            } catch (error) {
              progress.status = 'failed';
              progress.error = 'Failed to parse response';
              progressMapRef.current.set(tempId, progress);
              setUploadProgress(new Map(progressMapRef.current));
              reject(error);
            }
          } else {
            let errorData = {};
            try {
              errorData = JSON.parse(xhr.responseText);
            } catch {
              // Ignore parse errors
            }
            progress.status = 'failed';
            progress.error = (errorData as any).message || (errorData as any).detail || 'Upload failed';
            progressMapRef.current.set(tempId, progress);
            setUploadProgress(new Map(progressMapRef.current));
            reject(new Error(progress.error));
          }
        });

        xhr.addEventListener('error', () => {
          progress.status = 'failed';
          progress.error = 'Network error';
          progressMapRef.current.set(tempId, progress);
          setUploadProgress(new Map(progressMapRef.current));
          reject(new Error('Network error'));
        });

        const formData = new FormData();
        formData.append('file', file);
        
        const token = localStorage.getItem('token');
        xhr.open('POST', `${config.API_URL}/v1/ombudsman-decisions/evidence?signal_id=${signalId}`);
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        // This upload uses XMLHttpRequest, so the window.fetch acting-org
        // interceptor never runs — forward X-Acting-Org here or a custodian
        // acting as another org resolves signal_id in their home org (404).
        const actingOrg = getActingOrg();
        if (actingOrg) {
          xhr.setRequestHeader('X-Acting-Org', actingOrg.id);
        }
        xhr.send(formData);
      });
    } catch (error: any) {
      progress.status = 'failed';
      progress.error = error.message || 'Upload failed';
      progressMapRef.current.set(tempId, progress);
      setUploadProgress(new Map(progressMapRef.current));
      toast.error(`Failed to upload evidence: ${error.message || 'Unknown error'}`);
      throw error;
    }
  }, [signalId, fetchEvidence]);

  // Delete evidence
  const removeEvidence = useCallback(async (evidenceId: string) => {
    try {
      await deleteEvidence(evidenceId);
      await fetchEvidence();
      toast.success('Evidence deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete evidence:', error);
      toast.error(`Failed to delete evidence: ${error.message || 'Unknown error'}`);
      throw error;
    }
  }, [fetchEvidence]);

  return {
    evidence,
    loading,
    uploadProgress: Array.from(uploadProgress.values()),
    uploadEvidence,
    deleteEvidence: removeEvidence,
    refreshEvidence: fetchEvidence
  };
};

